import { Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatStorageBytes } from "@/domains/documents/services/buildingStorageQuota";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useBillingMoney } from "../hooks";
import { buildingQuoteTimingCopy } from "../buildingPlanCreation";
import {
  buildingPlanTierSchema,
  type BillingPlan,
  type BillingOverview,
  type BuildingPlanTier,
  type BuildingSubscription,
} from "../types";

type ModalSummary =
  | { kind: "change" }
  | { kind: "add"; quote: BillingOverview["add_building_quote"]; currency: string };

function planStorageLabel(plan: BillingPlan, unlimited: string) {
  return plan.storage_limit_bytes === null
    ? unlimited
    : formatStorageBytes(plan.storage_limit_bytes);
}

function PlanLimitRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-[12px]">
      <dt className="text-[#696b6c]">{label}</dt>
      <dd className="text-right font-medium text-[#262626]">{value}</dd>
    </div>
  );
}

function PlanAllowance({
  label,
  value,
  used,
  row = false,
}: {
  label: string;
  value: number | null;
  used?: number;
  row?: boolean;
}) {
  const copy = useLanguage().t.billing.plans;
  const template = used === undefined ? copy.allowance : copy.usage;
  const allowance =
    value === null
      ? copy.unlimited
      : fill(template, { count: String(value), used: String(used ?? 0) });
  if (row) return <PlanLimitRow label={label} value={allowance} />;
  return (
    <p className="text-xs text-muted-foreground">
      {label}: {allowance}
    </p>
  );
}

function PlanSummaryPrice({
  plan,
  presentation,
}: {
  plan: BillingPlan;
  presentation: ModalSummary;
}) {
  const { t } = useLanguage();
  const copy = t.billing.plans.summary;
  const money = useBillingMoney();
  if (presentation.kind === "add") {
    return (
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-xs text-[#696b6c]">{copy.monthlyTotal}</span>
        <p className="text-right text-sm text-[#262626]">
          <span className="font-semibold">
            + {money(presentation.quote.monthly_rate_cents, presentation.currency)}
          </span>{" "}
          <span className="text-xs">{copy.perMonth}</span>
        </p>
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-[#262626]">{t.billing.table.footerTotal}</span>
        <span className="text-xs text-[#696b6c]">{t.billing.summary.excludingVat}</span>
      </div>
      <p className="text-right text-sm text-[#262626]">
        <span className="font-semibold">{money(plan.rate_cents, "EUR")}</span>{" "}
        <span className="text-xs">{copy.perBuildingMonth}</span>
      </p>
    </div>
  );
}

export function BillingPlanInfo({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
      <Info className="ti-info-circle mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <p>{children}</p>
    </div>
  );
}

function ModalPlanInfo({ plan, presentation }: { plan: BillingPlan; presentation: ModalSummary }) {
  const { t } = useLanguage();
  const money = useBillingMoney();
  if (presentation.kind === "change")
    return <BillingPlanInfo>{t.billing.plans.changeTiming}</BillingPlanInfo>;
  const { quote, currency } = presentation;
  const detail =
    quote.monthly_rate_cents === 0
      ? plan.code === "free"
        ? t.billing.plans.freeLimit
        : t.billing.plans.noCard
      : fill(buildingQuoteTimingCopy(quote, t), {
          days: String(quote.billable_days),
          amount: money(quote.amount_cents, currency),
        });
  return <BillingPlanInfo>{detail}</BillingPlanInfo>;
}

function ModalPlanLimits({
  plan,
  usage,
}: {
  plan: BillingPlan;
  usage?: BuildingSubscription["usage"];
}) {
  const copy = useLanguage().t.billing.plans;
  const storage = planStorageLabel(plan, copy.unlimited);
  return (
    <dl className="space-y-2">
      <PlanLimitRow label={copy.storage} value={storage} />
      <PlanAllowance
        label={copy.projects}
        value={plan.max_projects}
        used={usage?.projects}
        row={true}
      />
      <PlanAllowance
        label={copy.summary.chatQuestions}
        value={plan.chat_questions_per_month}
        used={usage?.chat_questions}
        row={true}
      />
      <PlanAllowance
        label={copy.pmIssues}
        value={plan.max_pm_issues}
        used={usage?.pm_issues}
        row={true}
      />
    </dl>
  );
}

export function BillingPlanSummary({
  plan,
  usage,
  presentation,
}: {
  plan: BillingPlan;
  usage?: BuildingSubscription["usage"];
  presentation?: ModalSummary;
}) {
  const { t } = useLanguage();
  const money = useBillingMoney();
  const copy = t.billing.plans;
  const storage = planStorageLabel(plan, copy.unlimited);
  if (presentation) {
    return (
      <section
        aria-label={copy.summary.included}
        className="space-y-3 rounded-lg border border-border bg-muted/30 p-4"
      >
        <PlanSummaryPrice plan={plan} presentation={presentation} />
        <hr className="border-0 border-t border-border" />
        <h3 className="text-xs font-medium uppercase tracking-wide text-[#696b6c]">
          {copy.summary.included}
        </h3>
        <ModalPlanLimits plan={plan} usage={usage} />
      </section>
    );
  }
  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium">
        {money(plan.rate_cents, "EUR")} {t.billing.rate.perBuildingMonth}
      </p>
      <p className="text-xs text-muted-foreground">
        {copy.storage}: {storage}
      </p>
      <PlanAllowance label={copy.projects} value={plan.max_projects} used={usage?.projects} />
      <PlanAllowance
        label={copy.chatQuestions}
        value={plan.chat_questions_per_month}
        used={usage?.chat_questions}
      />
      <PlanAllowance label={copy.pmIssues} value={plan.max_pm_issues} used={usage?.pm_issues} />
      {plan.code === "free" && <p className="text-xs text-muted-foreground">{copy.freeLimit}</p>}
    </div>
  );
}

export function BuildingPlanChoice({
  plans,
  tier,
  onChange,
  disabled = false,
  presentation,
}: {
  plans: BillingPlan[];
  tier: BuildingPlanTier;
  onChange: (tier: BuildingPlanTier) => void;
  disabled?: boolean;
  presentation?: ModalSummary;
}) {
  const { t } = useLanguage();
  const money = useBillingMoney();
  const selected = plans.find((plan) => plan.code === tier);
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label
          htmlFor="building-plan-choice"
          className={presentation ? "text-xs uppercase tracking-wide" : undefined}
        >
          {t.billing.plans.plan}
        </Label>
        <Select
          value={tier}
          onValueChange={(value) => onChange(buildingPlanTierSchema.parse(value))}
          disabled={disabled}
        >
          <SelectTrigger id="building-plan-choice">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {plans.map((plan) => (
              <SelectItem key={plan.code} value={plan.code}>
                {t.billing.plans.names[plan.code]} · {money(plan.rate_cents, "EUR")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {selected && <BillingPlanSummary plan={selected} presentation={presentation} />}
      {presentation ? (
        selected && <ModalPlanInfo plan={selected} presentation={presentation} />
      ) : (
        <p className="text-xs text-muted-foreground">
          {tier === "free" ? t.billing.plans.noCard : t.billing.summary.excludingVat}
        </p>
      )}
    </div>
  );
}
